import type { SetStateAction } from 'react';
import React from 'react';

import type {
  AccordionElement,
  AccordionProps,
} from '@core/accordions/Accordion';

export type ControlledAccordionProps = Pick<
  AccordionProps,
  'expanded' | 'onChange'
>;

type AccordionOnChange = ControlledAccordionProps['onChange'];

type StateConfiguration = {
  readonly createInitialState: (
    expandedIndices: number[],
    length: number
  ) => () => boolean[];

  readonly createNextState: (
    accordionIndex: number,
    expanded: boolean
  ) => SetStateAction<boolean[]>;
};

const getSingleExpandStateConfig = (): StateConfiguration => ({
  createInitialState: (expandedIndices, length) => () => {
    const expandedIndex = expandedIndices.find((index) => index < length);
    return Array.from({ length }, (_, idx: number) => idx === expandedIndex);
  },

  createNextState: (accordionIndex, expanded) => (prevState) => {
    return expanded
      ? prevState.map((_, index) => index === accordionIndex)
      : prevState.map(() => false);
  },
});

const getMultiExpandStateConfig = (): StateConfiguration => ({
  createInitialState: (expandedIndices, length) => () => {
    return Array.from({ length }, (_, idx: number) =>
      expandedIndices.includes(idx)
    );
  },

  createNextState: (accordionIndex, expanded) => (prevState) => {
    const nextState = [...prevState];
    nextState[accordionIndex] = expanded;
    return nextState;
  },
});

const useControlledStateConfig = (useMultiExpandState: boolean) =>
  React.useMemo(
    () =>
      useMultiExpandState
        ? getMultiExpandStateConfig()
        : getSingleExpandStateConfig(),
    [useMultiExpandState]
  );

/**
 * Creates Accordion props that lets the AccordionGroup to control their expanded state.
 *
 * When children count increases after mount, new children default to collapsed (false).
 *
 */
const useControlledAccordions = (props: {
  defaultExpanded: number[];
  multiExpand: boolean;
  children: AccordionElement | AccordionElement[];
}): ControlledAccordionProps[] => {
  const { defaultExpanded, children, multiExpand } = props;
  const length = React.Children.count(children);

  const { createInitialState, createNextState } = useControlledStateConfig(
    multiExpand
  );

  const [expandedStates, setExpandedStates] = React.useState(
    createInitialState(defaultExpanded, length)
  );

  const onChangeHandlers: AccordionOnChange[] = React.useMemo(
    () =>
      Array.from({ length }, (_, index) => (_, expanded) => {
        setExpandedStates(createNextState(index, expanded));
      }),
    [length, setExpandedStates, createNextState]
  );

  // When children are added after mount, grow `expandedStates` so that new
  // children start collapsed and all state-transition logic (which maps over
  // prevState) operates on a correctly-sized array.
  React.useEffect(() => {
    setExpandedStates((prev) => {
      if (prev.length >= length) return prev;
      return [...prev, ...Array(length - prev.length).fill(false)];
    });
  }, [length]);

  // Iterate over the current `length` instead of `expandedStates` so that even
  // before the effect above fires the returned array always covers every child.
  // Without this guard, `controlledAccordionProps[index]` is `undefined` for
  // any new child whose index exceeds the original `expandedStates` length,
  // causing a TypeError crash in AccordionGroup.tsx.
  return Array.from({ length }, (_, index) => ({
    expanded: expandedStates[index] ?? false,
    onChange: onChangeHandlers[index],
  }));
};

export default useControlledAccordions;
